import React, { useCallback } from "react"
import { renderSourceIcon } from "~/ui-rtk/components/ui/charts/TableVisualization/components/Cell/utils"
import {
  TMetric,
  type TSource as TTableVSource,
} from "~/ui-rtk/constants/table-visualization-types"
import { SOURCE_IDS } from "~/ui-rtk/constants/sources"
import {
  safeDivide,
  toCoef,
  toNumber,
  toCurrency,
} from "~/ui-rtk/utils/number-utils"
import { createStatsItem, getSource } from "./utils"
import { TSource } from "~/ui-rtk/hooks/sources"

export type TStatsTotalHeader =
  | "Source"
  | "Spend"
  | "Brand Value"
  | "Brand ROAS"
  | "DR Revenue"
  | "DR ROAS"
  | "Total Value"
  | "Total ROAS"

export type TStatsMetaHeader =
  | "Source"
  | "Spend"
  | "Likes/Reactions"
  | "Comments"
  | "Shares"
  | "Saves"
  | "DR Revenue"
  | "DR ROAS"

export type TStatsTiktokHeader =
  | "Likes/Reactions"
  | "Comments"
  | "Shares"
  | "Empty"

export type TStatsHeader = TStatsTotalHeader | TStatsMetaHeader

export type TStatsTotalItem = Record<
  TStatsTotalHeader,
  string | number | React.ReactNode
>
export type TStatsMetaItem = Record<
  TStatsMetaHeader,
  string | number | React.ReactNode
>
export type TStatsTiktokItem = Record<
  TStatsTiktokHeader,
  string | number | React.ReactNode
>

export type TSourceId = keyof typeof SOURCE_IDS

export type TStatsItem = TStatsTotalItem | TStatsMetaItem

export const useStats = (metric: TMetric) => {
  const brandValueOverSpendKey = `brand_value_over_spend`
  const sourceName = (metric.media_channel as string).toUpperCase()
  const purchases = +(metric.purchases as number)
  const brandValue = +((metric.brand_value ?? metric.brand_value_agg) as number)
  const spendValue = +(metric.spend as number)

  const totalStats: Array<TStatsTotalItem> = []
  const metaStats: Array<TStatsMetaItem> = []
  const tiktokStats: Array<TStatsTiktokItem> = []

  const getMetricValue = (key: string, defaultValue = 0) =>
    metric[key] ?? defaultValue

  const createMetaStatsItem = (source: TSource, adType: string) => ({
    Source: renderSourceIcon({
      icon: source.icon,
      name: source.name,
    } as TTableVSource),
    Spend: toCurrency(+getMetricValue(`${adType}_spend`)),
    "Brand Value": toCurrency(+getMetricValue(`${adType}_brand_value`)),
    "Brand ROAS": toCoef(
      safeDivide(
        +getMetricValue(`${adType}_brand_value`),
        +getMetricValue(`${adType}_spend`),
      ),
    ),
    "DR Revenue": toCurrency(+getMetricValue(`${adType}_purchases`)),
    "DR ROAS": toCoef(
      safeDivide(
        +getMetricValue(`${adType}_purchases`),
        +getMetricValue(`${adType}_spend`),
      ),
    ),
    "Likes/Reactions": toNumber(
      +getMetricValue(
        source?.id === "FACEBOOK" ? `${adType}_reactions` : `${adType}_likes`,
      ),
    ),
    Comments: toNumber(+getMetricValue(`${adType}_comments`)),
    Shares: toNumber(+getMetricValue(`${adType}_shares`)),
    Saves: toNumber(+getMetricValue(`${adType}_saves`)),
    Follows: toNumber(
      +getMetricValue(
        adType === "facebook_ads" ? `${adType}_new_fans` : `${adType}_follows`,
      ),
    ),
  })

  const createTikTokStatItem = () => ({
    Empty: "",
    "Likes/Reactions": toNumber(+getMetricValue(`tiktok_ads_likes`)),
    Comments: toNumber(+getMetricValue(`tiktok_ads_comments`)),
    Shares: toNumber(+getMetricValue(`tiktok_ads_shares`)),
  })

  const handleTikTokStats = useCallback(() => {
    tiktokStats.push(createTikTokStatItem())
  }, [tiktokStats])

  const handleMetaStats = useCallback(() => {
    const facebookAdsItem = createMetaStatsItem(
      getSource(SOURCE_IDS.FACEBOOK),
      "facebook_ads",
    )

    const instagramAdsItem = createMetaStatsItem(
      getSource(SOURCE_IDS.INSTAGRAM),
      "instagram_ads",
    )

    metaStats.push(facebookAdsItem, instagramAdsItem)
  }, [metaStats])

  switch (sourceName) {
    case SOURCE_IDS.META: {
      totalStats.push(
        createStatsItem(
          getSource(SOURCE_IDS.META),
          spendValue,
          brandValue,
          purchases,
          getMetricValue(brandValueOverSpendKey) as number,
        ),
      )
      handleMetaStats()
      break
    }
    case SOURCE_IDS.TIKTOK_ADS: {
      totalStats.push(
        createStatsItem(
          getSource(SOURCE_IDS.TIKTOK_ADS),
          spendValue,
          brandValue,
          purchases,
          getMetricValue(brandValueOverSpendKey) as number,
        ),
      )
      handleTikTokStats()
      break
    }
  }

  return {
    totalStats,
    metaStats,
    tiktokStats,
  }
}
